import {BreadcrumbType, Form, FormSubmitHandler} from 'platform/components';
import {match} from 'ts-pattern';
import {object} from 'yup';

import {isNil, prop} from 'ramda';

import {
  ListContextParametersApiArg,
  TemplateTypeEnum,
  useGetTemplateQuery,
  useListContextParametersQuery,
  usePatchTemplateMutation,
} from '@dms/api/core';
import {i18n} from '@dms/i18n';
import {settingsRoutes, testIds} from '@dms/routes';
import {SettingsTemplate} from '@dms/settings/shared';
import {handleApiError} from '@dms/shared';

import {useNavigate, yupString, ApiError} from 'shared/web';

import {MessageTemplateFormValues} from '../types/MessageTemplateFormValues';
import {getContextOptions} from '../utils/getContextOptions';
import {MessageTemplateFields} from './MessageTemplateFields';

const breadcrumbs: BreadcrumbType[] = [
  {
    label: i18n.t('page.messageTemplates.labels.list'),
    href: settingsRoutes.messageTemplates,
  },
];

type Props = {
  templateType: TemplateTypeEnum;
  messageTemplateId: string;
};

export const EditMessageTemplateForm = (props: Props) => {
  const navigate = useNavigate();

  const {
    data: messageTemplate,
    isLoading: isLoadingEditData,
    isError: isErrorEditData,
  } = useGetTemplateQuery(
    {
      templateId: props.messageTemplateId || '',
      templateType: props.templateType,
    },
    {
      skip: isNil(props.messageTemplateId),
    }
  );

  const {isLoading: isLoadingContextParameters, isError: isErrorContextParameters} =
    useListContextParametersQuery(
      {
        context: messageTemplate?.context as ListContextParametersApiArg['context'],
      },
      {
        skip: isNil(messageTemplate?.context),
      }
    );

  const isLoading = isLoadingEditData || isLoadingContextParameters;
  const isError = isErrorEditData || isErrorContextParameters;

  const [patchTemplateMutation, {isLoading: isMutationInProgress}] = usePatchTemplateMutation();
  const onSubmit: FormSubmitHandler<MessageTemplateFormValues> = async (
    data: MessageTemplateFormValues,
    _setErrors,
    _reset,
    blockerContext
  ) => {
    await patchTemplateMutation({
      templateId: props.messageTemplateId,
      templateType: props.templateType,
      patchTemplateRequestBody: {
        ...data,
      },
    })
      .unwrap()
      .catch((e: ApiError) => handleApiError(e, {signalApiError: blockerContext?.reportApiFailure}))
      .then(() => {
        if (blockerContext?.wasSubmittedFromBlocker) {
          return;
        }
        navigate(settingsRoutes.messageTemplates);
      });
  };

  const schemaSMS = object({
    name: yupString.required(),
    context: yupString.required().oneOf(getContextOptions(props.templateType).map(prop('value'))),
    body: yupString.required(),
  });

  const schemaChat = schemaSMS;

  const schemaEmail = schemaSMS.shape({
    subject: yupString.required(),
  });

  return (
    <SettingsTemplate
      header={{
        breadcrumbs,
        title: i18n.t('page.messageTemplates.labels.edit', {
          templateType: i18n.t(`page.messageTemplates.labels.${props.templateType}`),
          templateName: messageTemplate?.name,
        }),
      }}
      isLoading={isLoading}
      isError={isError}
      description={i18n.t('page.messageTemplates.labels.newDescription')}
      data-testid={testIds.settings.messageTemplates(`${props.templateType}edit`)}
    >
      <Form<MessageTemplateFormValues>
        schema={match(props.templateType)
          .with('sms', () => schemaSMS)
          .with('chat', () => schemaChat)
          .with('email', () => schemaEmail)
          .exhaustive()}
        defaultValues={messageTemplate as MessageTemplateFormValues}
        onSubmit={onSubmit}
        shouldWatchForUnsavedChanges
      >
        {(control, formApi, navigateWithoutBlocker) => (
          <MessageTemplateFields
            formApi={formApi}
            control={control}
            templateType={props.templateType}
            isMutationInProgress={isMutationInProgress}
            onDiscard={() => navigateWithoutBlocker(settingsRoutes.messageTemplates)}
          />
        )}
      </Form>
    </SettingsTemplate>
  );
};
